<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed } from 'vue'
import { usePremiumStore } from '@state/stores/premium'
import Layout from '@layouts/main-layout'
import AudioQuality from './audio-quality'
import ExternalPlayer from './external-player'
import AboutFooterMenu from './../../../partials/about-footer-menu'

// `<component :is>` resolves a STRING against registered components, never against
// <script setup> bindings — the routes name the tab, so the name is mapped back here.
const MAIN_COMPONENTS = { AudioQuality, ExternalPlayer }

const props = defineProps({
    mainComponent: {
        type: String,
        default: 'AudioQuality',
    },
})

const premiumStore = usePremiumStore()

const mainView = computed(() => MAIN_COMPONENTS[props.mainComponent])

const isPremium = computed(() => premiumStore.isPremium)

const tabs = computed(() => [
    { to: { name: 'settings/audio-quality' }, label: 'Audio Quality' },
    {
        to: { name: 'settings/external-player' },
        label: isPremium.value ? 'External Players' : 'External Players (Premium only)',
    },
])

usePageHead({ title: 'Settings', description: 'Settings' })
</script>

<template>
    <Layout>
        <div class="settings-container">
            <BaseContent title="Settings">
                <BaseTabPanel
                    :tabs="tabs"
                    class="tabs"
                    is-underlined
                    is-unpadded
                    address-navigation
                />
                <component :is="mainView" />
            </BaseContent>
            <BaseSection
                padded
                class="settings-footer"
            >
                <div class="separator" />
                <AboutFooterMenu/>
            </BaseSection>
        </div>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.settings-container {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - $size-music-player - $size-menu-bar-height);
    padding-bottom: $s-xxl;
}

.tabs {
    margin-bottom: $s-xxl;
}

.settings-footer {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
    padding-top: $s-xxl;
    padding-bottom: $s-xxl;
}

.separator {
    position: relative;
    display: block;
    width: 100%;
    padding: $s-s 0;
    border-top: 1px solid $c-dusty-gray;
    opacity: 0.1;
}

@include desktop {
    .settings-container {
        min-height: calc(100vh - $size-music-player - $s-xl);
        padding-bottom: 0;
    }
}
</style>
